<template>
  <div class="pp-shell">
    <!-- ==================== Ambient background ==================== -->
    <div class="pp-bg" aria-hidden="true"></div>

    <main class="pp-main">
      <!-- ==================== Brand strip ==================== -->
      <header class="pp-brand">
        <div class="brand-lock">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
          </svg>
        </div>
        <span class="brand-copy">Secure payment</span>
        <span v-if="order && order.receiptId" class="brand-receipt mono">#{{ order.receiptId }}</span>
      </header>

      <!-- ==================== LOADING ==================== -->
      <section v-if="screen === 'loading'" class="pp-card">
        <div class="sk-hero"></div>
        <div class="sk-line"></div>
        <div class="sk-block"></div>
        <div class="sk-line sk-line-short"></div>
      </section>

      <!-- ==================== INACTIVE / NOT FOUND ==================== -->
      <section v-else-if="screen === 'inactive'" class="pp-card pp-card-center">
        <div class="state-icon state-icon-neutral">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8" stroke-linecap="round" />
          </svg>
        </div>
        <h1 class="state-title">This payment link is no longer active</h1>
        <p class="state-sub">The link may have been completed earlier or has been retired. If you believe this is a mistake, please reach out to the person or business that sent it to you.</p>
      </section>

      <!-- ==================== FAILED (cancelled) ==================== -->
      <section v-else-if="screen === 'failed'" class="pp-card pp-card-center">
        <div class="state-icon state-icon-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="9" />
            <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
          </svg>
        </div>
        <h1 class="state-title">{{ order.supersededBy ? 'This payment page was replaced' : 'This order was cancelled by the creator' }}</h1>
        <p class="state-sub">
          {{ order.supersededBy
            ? 'A new payment page was created for you. Please continue there — do not pay or submit your UTR on this page.'
            : 'No payment is expected for this order anymore. Please contact support.' }}
        </p>
        <div class="state-meta">
          <div class="meta-row"><span>Amount</span><strong>₹{{ formatMoney(order.amount) }}</strong></div>
          <div class="meta-row"><span>Receipt</span><strong class="mono">{{ order.receiptId }}</strong></div>
        </div>
        <button
          v-if="order.supersededBy"
          type="button"
          class="btn-primary btn-block"
          @click="goToSupersedingOrder"
        >Continue on your new payment page</button>
        <a v-if="order.returnUrl && !order.supersededBy" :href="order.returnUrl" class="btn-primary btn-block">Return to merchant</a>
      </section>

      <!-- ==================== APPROVED (receipt) ==================== -->
      <section v-else-if="screen === 'approved'" class="pp-card pp-card-center">
        <div class="success-burst">
          <span class="burst-ring"></span>
          <span class="burst-ring burst-ring-2"></span>
          <div class="success-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
              <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
        <h1 class="state-title">Payment confirmed</h1>
        <p class="state-sub">Your payment has been verified successfully. You're all set.</p>

        <div class="receipt-panel">
          <div class="receipt-amount">₹{{ formatMoney(order.amount) }}</div>
          <div class="receipt-grid">
            <div class="meta-row"><span>Receipt</span><strong class="mono">{{ order.receiptId }}</strong></div>
            <div class="meta-row" v-if="order.utr"><span>UTR</span><strong class="mono">{{ order.utr }}</strong></div>
            <div class="meta-row" v-if="order.updatedAt"><span>Confirmed at</span><strong>{{ formatDate(order.updatedAt) }}</strong></div>
          </div>
          <div class="receipt-verified">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Verified payment
          </div>
        </div>

        <template v-if="order.returnUrl">
          <a :href="order.returnUrl" class="btn-primary btn-block">
            Return to merchant{{ redirectSeconds > 0 ? ` · ${redirectSeconds}s` : '' }}
          </a>
          <p class="redirect-hint">Taking you back automatically…</p>
        </template>
        <p v-else class="redirect-hint">You can safely close this page now.</p>
      </section>

      <!-- ==================== VERIFYING ==================== -->
      <section v-else-if="screen === 'verifying'" class="pp-card pp-card-center">
        <div class="verify-pulse">
          <span class="pulse-ring"></span>
          <span class="pulse-ring pulse-ring-2"></span>
          <span class="pulse-ring pulse-ring-3"></span>
          <div class="verify-core">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M12 8v4l2.5 2.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
        <h1 class="state-title">We're confirming your payment</h1>
        <p class="state-sub">
          Your proof has been received and is being verified. This usually takes a few minutes —
          you can keep this page open or come back later using the same link.
        </p>

        <div class="verify-panel">
          <div class="meta-row"><span>Amount</span><strong>₹{{ formatMoney(order.amount) }}</strong></div>
          <div class="meta-row" v-if="submittedUtr"><span>UTR submitted</span><strong class="mono">{{ submittedUtr }}</strong></div>
          <div class="meta-row" v-if="order.hasScreenshot || localScreenshotSubmitted"><span>Screenshot</span><strong class="ok-chip">Received ✓</strong></div>
          <div class="meta-row"><span>Checking for</span><strong>{{ verifyElapsedLabel }}</strong></div>
        </div>

        <p v-if="verifySlow" class="verify-slow">
          This is taking a little longer than usual. Your proof is safe with us — verification
          continues even if you close this page.
        </p>

        <div class="verify-live">
          <span class="live-dot"></span> Checking status automatically
        </div>
      </section>

      <!-- ==================== CONFIRMING EXPIRY (micro-state) ==================== -->
      <section v-else-if="screen === 'confirming'" class="pp-card pp-card-center">
        <div class="verify-pulse">
          <span class="pulse-ring"></span>
          <div class="verify-core">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
        <h1 class="state-title">Time's up — confirming status</h1>
        <p class="state-sub">The payment window has just closed. We're fetching the final status of this order…</p>
        <div class="verify-live"><span class="live-dot"></span> Checking status automatically</div>
      </section>

      <!-- ==================== EXPIRED (terminal, message-only) ==================== -->
      <!-- Product decision (29072026): expired orders accept NO proof —
           terminal screen mirroring FAILED. Proof submitted BEFORE expiry
           still renders the VERIFYING screen (operators can approve
           expired orders), but nothing new can be submitted here. -->
      <section v-else-if="screen === 'expired'" class="pp-card pp-card-center">
        <div class="state-icon state-icon-warn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <h1 class="state-title">Payment window expired</h1>
        <p class="state-sub">
          The time window for this payment has ended and this order can no
          longer be paid. Please contact support.
        </p>
        <div class="state-meta">
          <div class="meta-row"><span>Amount</span><strong>₹{{ formatMoney(order.amount) }}</strong></div>
          <div class="meta-row"><span>Receipt</span><strong class="mono">{{ order.receiptId }}</strong></div>
        </div>
        <a v-if="order.returnUrl" :href="order.returnUrl" class="btn-primary btn-block">Return to merchant</a>
      </section>

      <!-- ==================== PROOF-ONLY (pending, not accepting payments) ==================== -->
      <section v-else-if="screen === 'proof-only'" class="pp-card">
        <!-- QR switch (09082026): this screen means the assigned bank is
             offline — the customer who has NOT paid yet needs a working QR
             more than anyone. Same bar as PAYABLE; a successful switch to a
             live bank flips this screen back to payable on re-fetch. -->
        <div v-if="order.qrSwitch && order.qrSwitch.enabled && !order.qrSwitch.locked" class="qr-switch-bar">
          <div class="qr-switch-row">
            <button
              v-for="s in order.qrSwitch.slots"
              :key="s.slot"
              type="button"
              class="qr-slot"
              :class="{ 'is-current': s.isCurrent, 'is-unavailable': !s.available }"
              :disabled="s.isCurrent || qrBusy || qrCooldown > 0"
              @click="switchQr(s.slot)"
            >
              QR {{ s.slot }}<span v-if="s.isCurrent" class="qr-slot-tag">current</span><span v-else-if="!s.available" class="qr-slot-tag">unavailable</span>
            </button>
            <button
              v-if="order.qrSwitch.canRequestNewQr"
              type="button"
              class="qr-slot qr-slot-new"
              :disabled="qrBusy || qrCooldown > 0"
              @click="switchQr('new')"
            >
              {{ qrBusy ? 'Switching…' : "Haven't paid yet? Get a working QR" }}
            </button>
          </div>
          <p class="qr-switch-note">
            <template v-if="qrCooldown > 0">Please wait {{ qrCooldown }}s before switching again. </template>
            Pay only the QR shown — the amount may change when you switch.
          </p>
          <p v-if="qrNotice" class="qr-switch-notice">{{ qrNotice }}</p>
        </div>
        <div class="pp-card-center">
          <div class="state-icon state-icon-navy">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <h1 class="state-title">Complete your payment verification</h1>
          <p class="state-sub">
            <b>Already paid?</b> Enter your UTR or upload the payment screenshot below to get verified.
          </p>
        </div>
        <div class="proof-wrap proof-wrap-open">
          <proof-form
            :is-loading="isSubmitting"
            :loading-label="submitPhase"
            :utr-error="proofError"
            @submit-utr="submitUtr"
            @submit-screenshot="submitScreenshot"
          />
        </div>
        <p class="expired-note">If you haven't completed the payment yet, please return to the merchant and try again.</p>
      </section>

      <!-- ==================== PAYABLE ==================== -->
      <section v-else-if="screen === 'payable'" class="pp-card">
        <!-- Amount hero + countdown -->
        <div class="pay-hero">
          <div class="hero-label">Amount to pay</div>
          <div class="hero-amount">₹{{ formatMoney(order.amount) }}</div>
          <div v-if="secondsLeft !== null" class="hero-countdown" :class="{ 'is-urgent': secondsLeft <= 120 }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>{{ countdownLabel }}</span>
          </div>
        </div>

        <!-- 3-step strip -->
        <ol class="steps">
          <li class="step" :class="{ 'is-current': !interacted, 'is-done': interacted }">
            <span class="step-dot">1</span><span class="step-label">Pay</span>
          </li>
          <li class="step" :class="{ 'is-current': interacted }">
            <span class="step-dot">2</span><span class="step-label">Submit UTR / screenshot</span>
          </li>
          <li class="step">
            <span class="step-dot">3</span><span class="step-label">Get confirmed</span>
          </li>
        </ol>

        <!-- ============ Payment section ============ -->
        <div class="pay-section">
          <!-- QR — always visible from load -->
          <div v-if="qrCodeText" class="qr-block">
            <div class="qr-frame">
              <qr-code ref="qrCode" :text="qrCodeText" :size="180" error-level="M"></qr-code>
            </div>
            <div class="qr-caption">Scan with any UPI app</div>
            <button type="button" class="qr-download" @click="downloadQr">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Save QR — open it from your UPI app's gallery scan
            </button>
          </div>

          <!-- Static bank QR image (Manual Bank rail) -->
          <div v-else-if="credentials && credentials.qr" class="qr-block">
            <div class="qr-frame"><img :src="credentials.qr" class="qr-img" alt="Payment QR" /></div>
            <div class="qr-caption">Scan with any UPI app</div>
          </div>

          <!-- UPI ID copy row -->
          <div v-if="payeeUpi" class="upi-row">
            <div class="upi-meta">
              <span class="upi-label">UPI ID</span>
              <span class="upi-value mono">{{ payeeUpi }}</span>
            </div>
            <button type="button" class="upi-copy" @click="copyUpi">
              <svg v-if="!upiCopied" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ upiCopied ? 'Copied' : 'Copy' }}
            </button>
          </div>

          <!-- QR switch (09082026): low-key escape hatch for the minority
               whose QR fails — embedded under the QR, chips only for the
               OTHER slots (the current one is the QR above). Unavailable
               slots stay tappable: the backend hands out a replacement. -->
          <div v-if="order.qrSwitch && order.qrSwitch.enabled && !order.qrSwitch.locked" class="qr-switch-inline">
            <span class="qr-switch-inline-label">QR not working?</span>
            <template v-for="s in order.qrSwitch.slots" :key="s.slot">
              <button
                v-if="!s.isCurrent"
                type="button"
                class="qr-mini-chip"
                :class="{ 'is-unavailable': !s.available }"
                :disabled="qrBusy || qrCooldown > 0"
                @click="switchQr(s.slot)"
              >Use QR {{ s.slot }}</button>
            </template>
            <button
              v-if="order.qrSwitch.canRequestNewQr"
              type="button"
              class="qr-mini-chip qr-mini-new"
              :disabled="qrBusy || qrCooldown > 0"
              @click="switchQr('new')"
            >{{ qrBusy ? 'Switching…' : 'Try another QR' }}</button>
            <span v-if="qrCooldown > 0" class="qr-mini-wait">wait {{ qrCooldown }}s</span>
          </div>
          <p v-if="order.qrSwitch && order.qrSwitch.enabled && !order.qrSwitch.locked && qrNotice" class="qr-switch-inline-notice">{{ qrNotice }}</p>

          <!-- App deep-link buttons -->
          <div v-if="payeeUpi && isMobile" class="apps-block">
            <div class="apps-title">Pay directly from an app</div>
            <div class="apps-grid">
              <a :href="gpayLink" class="app-btn" @click="trackApp('gpay')">GPay</a>
              <a :href="phonepeLink" class="app-btn" @click="trackApp('phonepe')">PhonePe</a>
              <a :href="paytmLink" class="app-btn" @click="trackApp('paytm')">Paytm</a>
              <a :href="paynowLink" class="app-btn" @click="trackApp('upi_intent')">Other UPI</a>
            </div>
            <p class="apps-fallback">
              App not working or payment failed there? Scan the QR or copy the UPI ID above.
            </p>
          </div>

          <!-- Bank transfer details (Manual Bank rail) -->
          <div v-if="credentials && credentials.accountNumber" class="bank-block">
            <div class="bank-title">Or pay via bank transfer</div>
            <div class="meta-row"><span>Account holder</span><strong>{{ credentials.accountHolderName }}</strong></div>
            <div class="meta-row"><span>Account no.</span><strong class="mono">{{ credentials.accountNumber }}</strong></div>
            <div class="meta-row"><span>IFSC</span><strong class="mono">{{ credentials.ifsc }}</strong></div>
            <div class="meta-row"><span>Bank</span><strong>{{ credentials.bankName }}</strong></div>
          </div>
        </div>

        <!-- ============ Step 2 — proof panel ============ -->
        <div ref="proofPanel" class="proof-wrap" :class="{ 'is-emphasized': interacted }">
          <div class="proof-head">
            <span class="proof-flash" v-if="interacted"></span>
            <div class="proof-title">
              {{ interacted ? 'Paid? Submit your proof now to get verified' : 'After paying, submit your proof here' }}
            </div>
            <div class="proof-sub">
              Payment alone doesn't complete this order — we need your UTR number or a payment
              screenshot to verify it.
            </div>
          </div>
          <proof-form
            :is-loading="isSubmitting"
            :loading-label="submitPhase"
            :utr-error="proofError"
            :show-utr="utrEnabled"
            @submit-utr="submitUtr"
            @submit-screenshot="submitScreenshot"
          />
        </div>

        <div class="secure-foot">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
          </svg>
          256-bit encrypted · Do not share this link with anyone
        </div>
      </section>

      <!-- ==================== Error banner (network etc.) ==================== -->
      <transition name="fade">
        <div v-if="banner" class="pp-banner" :class="'is-' + banner.type" role="status" aria-live="polite">
          <span>{{ banner.message }}</span>
          <button type="button" class="banner-close" @click="banner = null">×</button>
        </div>
      </transition>

      <!-- Submit-success morph overlay -->
      <transition name="fade">
        <div v-if="showSubmitBurst" class="submit-burst" role="status" aria-live="polite">
          <div class="burst-card">
            <div class="success-check success-check-sm">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="burst-title">Proof received</div>
            <div class="burst-sub">Starting verification…</div>
          </div>
        </div>
      </transition>
    </main>
  </div>
</template>

<script>
import http from "../../http-common.js";
import { getScopedSocket } from "../../utils/socket";
import VueQRCodeComponent from "vue-qrcode-component";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { defineComponent, h } from "vue";

/*
 * Inline proof form (UTR + screenshot) — used by PAYABLE, EXPIRED and
 * PROOF-ONLY screens. Kept as a local component so all three screens share
 * one implementation without a separate file. Emits:
 *   submit-utr(utr)          — digits-only UTR string
 *   submit-screenshot(file)  — image File <= 2MB
 */
const ProofForm = defineComponent({
  name: "proof-form",
  props: {
    isLoading: { type: Boolean, default: false },
    // Phase label shown on busy buttons ("Reading screenshot…" during OCR,
    // "Uploading…" during transfer). Empty → generic busy text.
    loadingLabel: { type: String, default: "" },
    utrError: { type: String, default: "" },
    // Bank-level is_utr_enabled gate. Screens without bank context
    // (expired / proof-only) leave it true so both proof rails show.
    showUtr: { type: Boolean, default: true },
  },
  emits: ["submit-utr", "submit-screenshot"],
  data() {
    return { utr: "", localMsg: "", file: null, preview: null };
  },
  beforeUnmount() {
    // The preview blob URL outlives nothing — release it with the form
    // (covers the unmount that follows every successful upload, when the
    // screen morphs to VERIFYING).
    this.revokePreview();
  },
  methods: {
    revokePreview() {
      if (this.preview) {
        try { URL.revokeObjectURL(this.preview); } catch (e) { void 0; }
        this.preview = null;
      }
    },
    onUtrInput(e) {
      // No maxlength attribute on the input — it would truncate a pasted
      // "UTR: <digits>" BEFORE this filter strips the label, silently
      // dropping trailing digits. Strip first, then cap at 22.
      const cleaned = String(e.target.value || "").replace(/[^0-9]/g, "").slice(0, 22);
      this.utr = cleaned;
      e.target.value = cleaned;
      this.localMsg = "";
    },
    submitU() {
      if (!this.utr) {
        this.localMsg = "Enter your UTR (digits only) — it's in your payment app's transaction details.";
        return;
      }
      this.$emit("submit-utr", this.utr);
    },
    pickFile() {
      this.$refs.fileInput.click();
    },
    onFile(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      const reject = (msg) => {
        this.localMsg = msg;
        // Reset the input so re-selecting the SAME filename (after the
        // customer fixes the file) still fires a change event.
        e.target.value = null;
      };
      if (!f.type.startsWith("image/")) {
        return reject("Only image files (JPG / PNG) are allowed.");
      }
      if (f.size > 2 * 1024 * 1024) {
        return reject("Screenshot must be under 2MB.");
      }
      this.localMsg = "";
      this.revokePreview(); // release the previous preview's blob URL
      this.file = f;
      this.preview = URL.createObjectURL(f);
    },
    clearFile() {
      this.file = null;
      this.revokePreview();
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },
    submitS() {
      if (!this.file) {
        this.localMsg = "Choose your payment screenshot first.";
        return;
      }
      this.$emit("submit-screenshot", this.file);
    },
  },
  render() {
    const err = this.utrError || this.localMsg;
    return h("div", { class: "pf" }, [
      this.showUtr
        ? h("div", { class: "pf-utr" }, [
            h("label", { class: "pf-label" }, "UTR / Transaction reference"),
            h("input", {
              class: "pf-input",
              type: "text",
              inputmode: "numeric",
              placeholder: "Enter UTR (digits only)",
              value: this.utr,
              disabled: this.isLoading,
              onInput: this.onUtrInput,
            }),
            h(
              "button",
              { class: "btn-primary btn-block", type: "button", disabled: this.isLoading, onClick: this.submitU },
              this.isLoading ? (this.loadingLabel || "Submitting…") : "Submit UTR"
            ),
          ])
        : null,
      this.showUtr ? h("div", { class: "pf-or" }, [h("span", "or")]) : null,
      h("div", { class: "pf-shot" }, [
        h("input", {
          ref: "fileInput",
          class: "pf-hidden",
          type: "file",
          accept: "image/*",
          onChange: this.onFile,
        }),
        this.preview
          ? h("div", { class: "pf-preview" }, [
              h("img", {
                src: this.preview,
                class: "pf-preview-img",
                alt: "Payment screenshot preview — tap to change",
                role: "button",
                tabindex: 0,
                onClick: this.pickFile,
                onKeydown: (e) => { if (e.key === "Enter" || e.key === " ") this.pickFile(); },
              }),
              h("div", { class: "pf-preview-actions" }, [
                h("button", { class: "btn-ghost", type: "button", onClick: this.clearFile, disabled: this.isLoading }, "Remove"),
                h(
                  "button",
                  { class: "btn-primary", type: "button", onClick: this.submitS, disabled: this.isLoading },
                  this.isLoading ? (this.loadingLabel || "Uploading…") : "Upload screenshot"
                ),
              ]),
            ])
          : h(
              "button",
              { class: "pf-drop", type: "button", onClick: this.pickFile, disabled: this.isLoading },
              [
                h("span", { class: "pf-drop-icon" }, "🖼"),
                h("span", { class: "pf-drop-title" }, "Upload payment screenshot"),
                h("span", { class: "pf-drop-sub" }, "JPG or PNG · up to 2MB"),
              ]
            ),
      ]),
      err ? h("p", { class: "pf-error" }, err) : null,
    ]);
  },
});

const POLL_FAST_MS = 5000;   // verifying / confirming
const POLL_SLOW_MS = 30000;  // payable safety net (socket is the fast path)
const VERIFY_SLOW_AFTER_MS = 10 * 60 * 1000;

export default {
  name: "customer-pay-page",
  // "qr-code" matches the registration name the legacy page shipped with —
  // proven against this library's component definition in production.
  components: { "qr-code": VueQRCodeComponent, ProofForm },
  data() {
    return {
      id: this.$route.params.id,
      order: null,
      notFound: false,
      loadFailed: false,
      // Local proof state — bridges the gap between a successful submit
      // and the next poll confirming it server-side.
      localUtrSubmitted: "",
      localScreenshotSubmitted: false,
      isSubmitting: false,
      proofError: "",
      showSubmitBurst: false,
      // Step-2 emphasis after any payment interaction.
      interacted: false,
      upiCopied: false,
      // Countdown (server-anchored).
      secondsLeft: null,
      countdownTimer: null,
      countdownDone: false,
      // Customer QR switch.
      qrBusy: false,
      qrCooldown: 0,
      qrCooldownTimer: null,
      qrNotice: "",
      // Polling + sockets.
      pollTimer: null,
      socket: null,
      // Verifying elapsed + slow hint.
      verifyStartedAt: null,
      verifyElapsedLabel: "a few seconds",
      verifySlow: false,
      verifyTicker: null,
      // Approved auto-redirect.
      redirectSeconds: 0,
      redirectTimer: null,
      banner: null,
      // Teardown / async-safety plumbing.
      destroyed: false,
      retryTimer: null,
      burstTimer: null,
      // Monotonic fetch sequence — a slower, earlier response must never
      // overwrite a newer one (poll + socket fetches can overlap).
      fetchSeq: 0,
      // ProofForm phase label while the screenshot path runs OCR → upload.
      submitPhase: "",
    };
  },
  computed: {
    credentials() {
      return (this.order && this.order.paymentCredentials) || null;
    },
    payeeUpi() {
      return (this.credentials && this.credentials.payeeUPIID) || "";
    },
    utrEnabled() {
      // Bank-level toggle; default to true when the flag is absent so the
      // proof rail never silently disappears.
      if (!this.credentials) return true;
      const v = this.credentials.is_utr_enabled;
      return v === undefined || v === null ? true : !!Number(v);
    },
    hasProof() {
      if (!this.order) return false;
      return !!(
        this.order.customerUtr ||
        this.order.hasScreenshot ||
        this.localUtrSubmitted ||
        this.localScreenshotSubmitted
      );
    },
    submittedUtr() {
      return (this.order && this.order.customerUtr) || this.localUtrSubmitted || "";
    },
    screen() {
      if (this.notFound) return "inactive";
      if (!this.order) return "loading";
      const s = this.order.paymentStatus;
      // 'success' is the terminal state the instant-payout matching flow
      // stamps on payins (completePayinMatching.js) — for the customer it
      // means exactly what 'approved' means.
      if (s === "approved" || s === "success") return "approved";
      if (s === "failed") return "failed";
      if (s === "expired") return this.hasProof ? "verifying" : "expired";
      // pending
      if (this.hasProof) return "verifying";
      if (this.countdownDone) return "confirming";
      if (this.order.acceptingPayments) return "payable";
      return "proof-only";
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    },
    countdownLabel() {
      const s = this.secondsLeft;
      if (s === null) return "";
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const mm = String(m).padStart(2, "0");
      const ss = String(sec).padStart(2, "0");
      return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
    },
    qrCodeText() {
      if (!this.order || !this.payeeUpi) return "";
      const pn = this.order.clientName || "Payment";
      const tr = this.order.receiptId || this.order.refID;
      return `upi://pay?pa=${this.payeeUpi}&pn=${pn}&tr=${tr}&tn=${tr}&am=${this.order.amount}&cu=INR`;
    },
    paynowLink() {
      return this.qrCodeText || `upi://pay?pa=${this.payeeUpi}`;
    },
    gpayLink() {
      return this.buildAppLink("gpay://upi/pay");
    },
    paytmLink() {
      return this.buildAppLink("paytmmp://pay");
    },
    phonepeLink() {
      return this.buildAppLink("phonepe://pay");
    },
  },
  watch: {
    screen(next, prev) {
      this.onScreenChange(next, prev);
    },
  },
  async created() {
    // Tesseract.js from CDN — the screenshot proof path OCRs the image
    // client-side to extract the UTR the backend REQUIRES alongside the
    // file (uploadScreenshot.js rejects uploads without a parseable UTR).
    // Same loader + version the legacy page shipped with.
    if (!window.Tesseract) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";
      script.async = true;
      document.head.appendChild(script);
    }
    await this.fetchOrder(true);
    this.connectSocket();
    // NOTE: no explicit onScreenChange call here — the screen watcher
    // fires on the loading→X transition the fetch above causes. Calling
    // it again here double-initialized the screen (two redirect timers on
    // a revisited approved order → 2x-speed countdown).
    // Preserve the legacy customer-UPI acknowledgement the old page fired
    // on load (backend records the customer's UPI against the order).
    this.ackCustomerUpi();
  },
  beforeUnmount() {
    // Ordering matters: the destroyed flag first, so any in-flight
    // callback (poll response, burst timeout, load retry) that fires
    // during teardown early-returns instead of touching dead state.
    this.destroyed = true;
    this.stopPolling();
    clearInterval(this.qrCooldownTimer);
    this.stopCountdown();
    this.stopVerifyTicker();
    if (this.redirectTimer) clearInterval(this.redirectTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.burstTimer) clearTimeout(this.burstTimer);
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  },
  methods: {
    // Superseded order → jump to the replacement page. Full reload on
    // purpose: this component has no $route watcher (created() runs once),
    // so an in-app navigation would keep polling the OLD refID.
    goToSupersedingOrder() {
      if (!this.order || !this.order.supersededBy) return;
      window.location.hash = `#/pay/${this.order.supersededBy}`;
      window.location.reload();
    },
    startQrCooldown(seconds) {
      clearInterval(this.qrCooldownTimer);
      this.qrCooldown = Math.max(0, Math.ceil(seconds || 0));
      if (this.qrCooldown <= 0) return;
      this.qrCooldownTimer = setInterval(() => {
        this.qrCooldown -= 1;
        if (this.qrCooldown <= 0) clearInterval(this.qrCooldownTimer);
      }, 1000);
    },
    async switchQr(target) {
      if (this.qrBusy || this.qrCooldown > 0) return;
      this.qrBusy = true;
      this.qrNotice = "";
      const prevAmount = this.order ? this.order.amount : null;
      try {
        const res = await http.post(`/orders/${this.id}/switch-payin-bank`, { target });
        const data = (res.data && res.data.data) || {};
        if (data.switched) {
          this.startQrCooldown(8);
          await this.fetchOrder();
          // Every payment surface derives from the fresh order (QR canvas,
          // UPI row, app links, bank details) — but interaction state was
          // earned against the OLD bank's credentials and must not carry
          // over: no "Copied" badge for a UPI the customer never copied,
          // no step-2 emphasis from taps on the previous QR.
          this.upiCopied = false;
          this.interacted = false;
          if (data.amountChanged && this.order && this.order.amount !== prevAmount) {
            this.qrNotice = `New QR active — pay exactly ₹${this.formatMoney(this.order.amount)} on this QR only.`;
          } else {
            this.qrNotice = "New QR active — pay on this QR only.";
          }
        } else if (data.available === false) {
          this.qrNotice = res.data.message || "No QR is available right now. Please try again in a moment.";
        } else if (data.alreadyActive) {
          this.qrNotice = "";
        }
      } catch (err) {
        const status = err.response && err.response.status;
        if (status === 429) {
          this.qrNotice = "Please wait a moment and try again.";
          this.startQrCooldown(8);
        } else if (status === 409) {
          this.qrNotice = "The order just changed — refreshing.";
          await this.fetchOrder();
        } else {
          this.qrNotice = "Could not switch QR right now. Please try again.";
        }
      } finally {
        this.qrBusy = false;
      }
    },
    /* ---------------- data ---------------- */
    async fetchOrder(first = false) {
      if (this.destroyed) return;
      const seq = ++this.fetchSeq;
      try {
        const response = await http.get(`/orders/${this.id}`, { params: { apiType: "payment_v2" } });
        // Stale-response guard: only the newest request may write state.
        // Without this, a slow poll resolving after a socket-triggered
        // fetch could briefly regress an approved receipt back to pending.
        if (this.destroyed || seq !== this.fetchSeq) return;
        if (response.status === 200) {
          this.order = response.data;
          // Server-authoritative switch cooldown (survives reloads/instances).
          const qrCd = this.order.qrSwitch && this.order.qrSwitch.cooldownSecondsRemaining;
          if (qrCd && qrCd > this.qrCooldown) this.startQrCooldown(qrCd);
          if (this.loadFailed) {
            // Recovered from a failed initial load — clear the sticky banner.
            this.loadFailed = false;
            this.banner = null;
          }
          if (
            this.order.paymentStatus === "pending" &&
            typeof this.order.secondsRemaining === "number"
          ) {
            this.syncCountdown(this.order.secondsRemaining);
          }
        }
      } catch (error) {
        if (this.destroyed || seq !== this.fetchSeq) return;
        const code = error && error.response && error.response.status;
        if (code === 404) {
          this.notFound = true;
        } else if (first) {
          this.loadFailed = true;
          this.banner = { type: "error", message: "Couldn't load this payment. Retrying…" };
          this.retryTimer = setTimeout(() => this.fetchOrder(true), 4000);
        }
        // Poll errors are silent — next tick retries.
      }
    },
    /* ---------------- screen lifecycle ---------------- */
    onScreenChange(next, prev) {
      if (next === prev) return;
      this.stopPolling();
      if (next === "verifying" || next === "confirming") {
        this.startPolling(POLL_FAST_MS);
        if (next === "verifying") this.startVerifyTicker();
      } else {
        this.stopVerifyTicker();
      }
      if (next === "payable" || next === "proof-only") {
        this.startPolling(POLL_SLOW_MS);
      }
      if (next === "approved") {
        this.celebrate();
        this.startRedirectCountdown();
      }
      // Countdown keeps ticking through payable/proof-only/verifying (it
      // drives the expiry micro-state for all of them) and stops only on
      // terminal screens. Stopping it on proof-only caused a churn loop:
      // every poll's syncCountdown restarted what this line had stopped.
      if (["approved", "failed", "expired", "inactive"].includes(next)) {
        this.stopCountdown();
      }
    },
    startPolling(ms) {
      this.stopPolling();
      this.pollTimer = setInterval(() => this.fetchOrder(), ms);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    /* ---------------- countdown ---------------- */
    syncCountdown(seconds) {
      this.secondsLeft = seconds;
      if (seconds <= 0) {
        this.countdownDone = true;
        return;
      }
      // Server says time remains → un-latch. Recovers the page from the
      // 'confirming' trap when a stale response latched countdownDone or
      // ops extended the order's expires_at server-side.
      if (this.countdownDone) this.countdownDone = false;
      if (!this.countdownTimer) {
        this.countdownTimer = setInterval(() => {
          if (this.secondsLeft === null) return;
          this.secondsLeft = Math.max(0, this.secondsLeft - 1);
          if (this.secondsLeft === 0) {
            // Time's up locally — the page NEVER writes status. Flip to the
            // confirming micro-state and let polling fetch the cron's verdict.
            this.countdownDone = true;
            this.stopCountdown();
          }
        }, 1000);
      }
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    /* ---------------- verifying ticker ---------------- */
    startVerifyTicker() {
      if (this.verifyTicker) return;
      if (!this.verifyStartedAt) this.verifyStartedAt = Date.now();
      this.verifyTicker = setInterval(() => {
        const ms = Date.now() - this.verifyStartedAt;
        const mins = Math.floor(ms / 60000);
        if (mins < 1) this.verifyElapsedLabel = "less than a minute";
        else this.verifyElapsedLabel = `${mins} minute${mins === 1 ? "" : "s"}`;
        this.verifySlow = ms >= VERIFY_SLOW_AFTER_MS;
      }, 5000);
    },
    stopVerifyTicker() {
      if (this.verifyTicker) {
        clearInterval(this.verifyTicker);
        this.verifyTicker = null;
      }
    },
    /* ---------------- sockets ---------------- */
    connectSocket() {
      try {
        this.socket = getScopedSocket();
        let armed = false;
        this.socket.on("connect", () => {
          // Approval + expiry emits are global (no room join needed) —
          // chat/presence rooms from the old page are deprecated. Listeners
          // attach ONCE per socket instance; reconnects reuse them (the
          // `armed` guard prevents duplicate handlers stacking per reconnect).
          if (armed) return;
          const arm = () => {
            // Socket is nulled on unmount — the retry loop must die with
            // it instead of dereferencing null on the next tick.
            if (this.destroyed || !this.socket) return;
            if (this.notFound) return; // dead link — nothing to listen for
            const vendor = this.order && this.order.vendor;
            if (!vendor) return setTimeout(arm, 1000);
            if (armed) return;
            armed = true;
            this.socket.on(`${vendor}-order-approved-${this.id}`, () => this.fetchOrder());
            this.socket.on(`auto-order-approved-${this.id}`, () => this.fetchOrder());
            this.socket.on(`${vendor}-payin-expired`, (data) => {
              if (data && data.refID === this.id) this.fetchOrder();
            });
          };
          arm();
        });
      } catch (e) {
        // Socket is a fast path only — polling covers everything.
      }
    },
    /* ---------------- proof submission ---------------- */
    async submitUtr(utr) {
      this.proofError = "";
      this.isSubmitting = true;
      try {
        const response = await http.post(`/orders/${this.id}/addCustomerUtr`, { customerUtr: utr });
        if (response.data && response.data.status) {
          this.localUtrSubmitted = utr;
          this.burstThenVerify();
        } else {
          this.proofError = (response.data && response.data.message) || "Couldn't submit UTR. Please try again.";
        }
      } catch (error) {
        this.proofError =
          (error.response && error.response.data && error.response.data.message) ||
          "Couldn't submit UTR. Please check your connection and try again.";
      }
      this.isSubmitting = false;
    },
    async submitScreenshot(file) {
      // Backend contract (uploadScreenshot.js): the screenshot MUST arrive
      // with a parseable UTR in `utr` — the server stores it as
      // customerUtr. We OCR the image client-side (Tesseract, same
      // pipeline the legacy page shipped) and extract UTR + amount before
      // uploading. No UTR found → reject locally with the same guidance
      // the backend would give.
      this.proofError = "";
      this.isSubmitting = true;
      this.submitPhase = "Reading screenshot…";
      try {
        if (!window.Tesseract) {
          this.proofError = "Screenshot reader is still loading — please try again in a few seconds, or use 'Enter UTR'.";
          this.isSubmitting = false;
          this.submitPhase = "";
          return;
        }
        const { data: { text } } = await window.Tesseract.recognize(file, "eng", {
          tessedit_char_whitelist:
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789₹.,:/- ",
        });
        const extractedUTR = this.extractUTRFromText(text);
        const extractedAmount = this.extractAmountFromText(text);
        if (!extractedUTR) {
          this.proofError = "Could not find a UTR in the screenshot. Please upload a clearer image or use 'Enter UTR'.";
          this.isSubmitting = false;
          this.submitPhase = "";
          return;
        }

        this.submitPhase = "Uploading…";
        const formData = new FormData();
        formData.append("upload_screenshot", file);
        formData.append("utr", extractedUTR);
        formData.append("amount", extractedAmount);
        const response = await http.post(`orders/${this.id}/upload-screenshot`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // STRICT success check. The backend answers HTTP 201 for failures
        // too (duplicate UTR, UTR already set, order not found) with
        // {status:false, message} — only an explicit status:true is a win.
        if (response.data && response.data.status === true) {
          this.localScreenshotSubmitted = true;
          this.localUtrSubmitted = extractedUTR;
          this.burstThenVerify();
        } else {
          this.proofError = (response.data && response.data.message) || "Upload failed. Please try again.";
        }
      } catch (error) {
        this.proofError =
          (error.response && error.response.data && error.response.data.message) ||
          "Upload failed. Please check your connection and try again.";
      }
      this.isSubmitting = false;
      this.submitPhase = "";
    },
    /**
     * Extract UTR (Unique Transaction Reference) from OCR text.
     * Handles patterns from PhonePe, Paytm, GPay, and generic UPI apps.
     * Also handles Tesseract inserting spaces in the middle of long numbers.
     * Ported verbatim from the legacy page (index.backup-29072026.vue).
     */
    extractUTRFromText(text) {
      // Normalize whitespace for easier matching
      const normalized = text.replace(/\s+/g, ' ');

      /**
       * After a UTR label is found, grab everything after it and collapse
       * all spaces between digit groups so "4416342 52587" becomes "441634252587".
       * Stops when it hits a non-digit/non-space character.
       */
      const collapseDigits = (str) => {
        // Grab leading digit groups separated by single spaces
        const match = str.match(/^[\d][\d\s]*/);
        if (!match) return null;
        const collapsed = match[0].replace(/\s+/g, '');
        // Must be at least 10 chars to be a valid UTR
        return collapsed.length >= 10 ? collapsed : null;
      };

      // Priority 1: labeled UTR patterns (PhonePe: "UTR: 334472306074" or "UTR:920648123127")
      const utrLabelMatch = normalized.match(/UTR[:\s#]*([0-9][\d\s]{9,})/i);
      if (utrLabelMatch) {
        const collapsed = collapseDigits(utrLabelMatch[1]);
        if (collapsed) return collapsed;
      }

      // Priority 2: UPI Ref No / UPI Reference ID (Paytm: "UPI Ref. No: 4416342 52587")
      const upiRefMatch = normalized.match(/UPI\s*Ref(?:erence)?(?:\s*(?:No|ID|Number))?[.:\s#]*([0-9][\d\s]{9,})/i);
      if (upiRefMatch) {
        const collapsed = collapseDigits(upiRefMatch[1]);
        if (collapsed) return collapsed;
      }

      // Priority 3: UPI transaction ID label (GPay: "UPI transaction ID\n905810446169")
      const upiTxnMatch = normalized.match(/UPI\s*transaction\s*ID[:\s]*([0-9][\d\s]{9,})/i);
      if (upiTxnMatch) {
        const collapsed = collapseDigits(upiTxnMatch[1]);
        if (collapsed) return collapsed;
      }

      // Priority 4: Transaction ID label — alphanumeric (PhonePe "T23...", "P19...")
      const txnIdMatch = normalized.match(/Transaction\s*ID[:\s]*([A-Z][A-Z0-9]{10,24})/i);
      if (txnIdMatch) return txnIdMatch[1].trim();

      // Priority 5: Fallback — standalone 12-digit number (standard UTR length)
      const twelveDigitMatch = normalized.match(/\b(\d{12})\b/);
      if (twelveDigitMatch) return twelveDigitMatch[1];

      // Priority 6: Fallback — standalone 10–15 digit number
      const genericMatch = normalized.match(/\b(\d{10,15})\b/);
      if (genericMatch) return genericMatch[1];

      return null;
    },

    /**
     * Convert spelled-out Indian currency text to a number.
     * e.g. "Rupees Five Hundred Only" → 500
     *      "Rupees One Thousand Two Hundred Ninety Only" → 1290
     *      "Rupees One Lakh Only" → 100000
     */
    parseSpelledAmount(text) {
      const ones = {
        zero:0,one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,
        ten:10,eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,
        seventeen:17,eighteen:18,nineteen:19
      };
      const tens = {
        twenty:20,thirty:30,forty:40,fifty:50,sixty:60,seventy:70,eighty:80,ninety:90
      };
      const scales = { hundred:100, thousand:1000, lakh:100000, crore:10000000 };

      // Extract the spelled-out portion between "Rupees" and "Only"
      const spelledMatch = text.match(/Rupees?\s+([\w\s]+?)\s+Only/i);
      if (!spelledMatch) return null;

      const words = spelledMatch[1].toLowerCase().trim().split(/\s+/);
      let total = 0;
      let current = 0;

      for (const word of words) {
        if (ones[word] !== undefined) {
          current += ones[word];
        } else if (tens[word] !== undefined) {
          current += tens[word];
        } else if (word === 'hundred') {
          current = current === 0 ? 100 : current * 100;
        } else if (scales[word] !== undefined) {
          current *= scales[word];
          total += current;
          current = 0;
        }
        // ignore unrecognized words (e.g. "and", "paisa")
      }

      total += current;
      return total > 0 ? total : null;
    },

    /**
     * Extract payment amount from OCR text.
     * Handles ₹, Rs., INR prefixes and comma-formatted numbers.
     * Also handles all known Tesseract misreads of the ₹ symbol.
     * Falls back to parsing spelled-out amount ("Rupees Five Hundred Only").
     *
     * Known ₹ misreads by Tesseract:
     *   ¥  - yen sign (very common, unicode proximity)
     *   €  - euro sign
     *   z, Z - visual similarity in certain fonts
     *   %  - similar stroke pattern
     *   f  - bold/italic fonts
     *   &  - low resolution
     *   t, T - blurry screenshots
     *   `  - backtick
     *   '  - apostrophe
     *   (empty) - Tesseract drops the symbol entirely
     */
    extractAmountFromText(text) {
      const normalized = text.replace(/\s+/g, ' ');

      /**
       * Validate a parsed number is a plausible payment amount.
       * Rejects phone numbers (10 digits), overly long numbers, and zero/negative.
       */
      const isValidAmount = (num, rawStr) => {
        if (isNaN(num) || num <= 0) return false;
        const digitsOnly = rawStr.replace(/[^0-9]/g, '');
        if (digitsOnly.length === 10) return false; // phone number
        if (digitsOnly.length >= 11) return false;  // too long
        if (num > 10000000) return false;
        return true;
      };

      // Priority 1: correct rupee symbol or Rs./INR prefix
      // e.g. ₹193.78 / ₹1,290 / ₹1 / Rs. 500 / INR 871
      const correctRupeePattern = /(?:₹|Rs\.?|INR)\s*([\d,]+(?:\.\d{1,2})?)/gi;
      let m;
      while ((m = correctRupeePattern.exec(normalized)) !== null) {
        const raw = m[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 2: ¥ specifically (very common misread, handle before generic misreads)
      const yenPattern = /¥\s*([\d,]+(?:\.\d{1,2})?)/g;
      while ((m = yenPattern.exec(normalized)) !== null) {
        const raw = m[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 3: € misread
      const euroPattern = /€\s*([\d,]+(?:\.\d{1,2})?)/g;
      while ((m = euroPattern.exec(normalized)) !== null) {
        const raw = m[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 4: other single-char misreads (z Z % f & t T ` ')
      // Must be preceded by whitespace/start and followed by digits to avoid false positives
      const otherMisreadPattern = /(?:^|\s)[zZ%f&tT`']\s*([\d,]+(?:\.\d{1,2})?)(?:\s|$)/g;
      while ((m = otherMisreadPattern.exec(normalized)) !== null) {
        const raw = m[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 5: "Amount" label followed by a number
      const labeledPattern = /Amount[:\s]*([\d,]+(?:\.\d{1,2})?)/i;
      const labeledMatch = normalized.match(labeledPattern);
      if (labeledMatch) {
        const raw = labeledMatch[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 6: "Debited from" section — amount after masked account number
      // e.g. "******5246  ₹1" or "XXXXXXXXXX9362  ₹193.78"
      const debitedPattern = /(?:\*{4,}|X{4,})\d{2,6}\s+(?:[₹¥€Rs.]|\s)?\s*([\d,]+(?:\.\d{1,2})?)/i;
      const debitedMatch = normalized.match(debitedPattern);
      if (debitedMatch) {
        const raw = debitedMatch[1].replace(/,/g, '');
        const parsed = parseFloat(raw);
        if (isValidAmount(parsed, raw)) return parsed.toFixed(2);
      }

      // Priority 7: spelled-out amount fallback — "Rupees Five Hundred Only"
      // Used when Tesseract skips the ₹ symbol line entirely but reads the word form
      const spelledAmount = this.parseSpelledAmount(normalized);
      if (spelledAmount) return parseFloat(spelledAmount).toFixed(2);

      return null;
    },

    burstThenVerify() {
      // Submit micro-animation → the screen computed flips to VERIFYING via
      // hasProof; the overlay covers the morph so it reads as one motion.
      this.showSubmitBurst = true;
      this.verifyStartedAt = Date.now();
      this.burstTimer = setTimeout(() => {
        if (this.destroyed) return;
        this.showSubmitBurst = false;
        this.fetchOrder();
      }, 1400);
    },
    /* ---------------- payable interactions ---------------- */
    buildAppLink(scheme) {
      if (!this.order || !this.payeeUpi) return "#";
      const pn = this.order.clientName || "Payment";
      const tr = this.order.receiptId || this.order.refID;
      return `${scheme}?pa=${this.payeeUpi}&pn=${pn}&tr=${tr}&tn=${tr}&am=${this.order.amount}&cu=INR`;
    },
    trackApp(app) {
      this.markInteracted();
      this.track(app);
    },
    async copyUpi() {
      try {
        await navigator.clipboard.writeText(this.payeeUpi);
        this.upiCopied = true;
        setTimeout(() => (this.upiCopied = false), 2000);
      } catch (e) {
        // Clipboard may be blocked in some webviews — non-fatal.
      }
      this.markInteracted();
      this.track("copy_to_clipboard");
    },
    downloadQr() {
      const el = this.$refs.qrCode && this.$refs.qrCode.$el;
      if (el) {
        html2canvas(el).then((canvas) => {
          saveAs(canvas.toDataURL("image/png"), "payment-qr.png");
        });
      }
      this.markInteracted();
      this.track("download_qr");
    },
    markInteracted() {
      if (this.interacted) return;
      this.interacted = true;
      this.$nextTick(() => {
        const panel = this.$refs.proofPanel;
        if (panel && panel.scrollIntoView) {
          setTimeout(() => panel.scrollIntoView({ behavior: "smooth", block: "center" }), 600);
        }
      });
    },
    async track(customerPaymentType) {
      try {
        await http.post(`/orders/${this.id}/setCustomerPaymentType`, { customerPaymentType });
      } catch (e) {
        // Analytics only — never surface.
      }
    },
    async ackCustomerUpi() {
      // Legacy parity: the old page auto-posted the order's customerUPIID
      // back on load (skipping the ask-your-UPI step). Fire-and-forget.
      try {
        const upi = this.order && this.order.customerUPIID;
        await http.post(`/orders/${this.id}/customerUpiId`, { customerUPIID: upi || "" });
      } catch (e) {
        void 0;
      }
    },
    /* ---------------- approved ---------------- */
    celebrate() {
      try {
        this.$confetti.start();
        setTimeout(() => this.$confetti.stop(), 2500);
      } catch (e) {
        void 0;
      }
    },
    startRedirectCountdown() {
      if (!this.order || !this.order.returnUrl) return;
      // Defensive: never stack a second interval (double-speed countdown).
      if (this.redirectTimer) clearInterval(this.redirectTimer);
      this.redirectSeconds = 5;
      this.redirectTimer = setInterval(() => {
        this.redirectSeconds -= 1;
        if (this.redirectSeconds <= 0) {
          clearInterval(this.redirectTimer);
          window.location.href = this.order.returnUrl;
        }
      }, 1000);
    },
    /* ---------------- misc ---------------- */
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatDate(input) {
      if (!input) return "";
      const clean = String(input).replace("T", " ").replace(".000Z", "");
      const d = new Date(clean);
      if (isNaN(d.getTime())) return String(input);
      return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.pp-shell {
  min-height: 100vh;
  min-height: 100dvh;
  position: relative;
  font-family: inherit;
  color: #1b2a47;
  display: flex;
  justify-content: center;
}
.pp-bg {
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at 18% 10%, rgba(255, 215, 168, 0.28), transparent 55%),
    radial-gradient(circle at 85% 90%, rgba(199, 52, 67, 0.05), transparent 50%),
    linear-gradient(160deg, #fbfaf7 0%, #f4f0e5 60%, #ede5d2 100%);
  z-index: 0;
}
.pp-main {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: 16px 14px 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ==================== Brand strip ==================== */
.pp-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
}
.brand-lock {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1b2a47, #2c3e63);
  color: #ffd7a8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.brand-lock svg { width: 13px; height: 13px; }
.brand-copy {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8371;
}
.brand-receipt {
  margin-left: auto;
  font-size: 11px;
  color: #a8a08c;
}

/* ==================== Card ==================== */
.pp-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 20px;
  padding: 22px 18px;
  box-shadow: 0 24px 50px -30px rgba(27, 42, 71, 0.25);
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pp-card-center {
  align-items: center;
  text-align: center;
}

/* ==================== Skeleton ==================== */
.sk-hero, .sk-line, .sk-block {
  border-radius: 10px;
  background: linear-gradient(90deg, #f4f0e5 25%, #faf7ef 50%, #f4f0e5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.sk-hero { height: 72px; }
.sk-line { height: 14px; }
.sk-line-short { width: 60%; }
.sk-block { height: 200px; }
@keyframes shimmer { to { background-position: -200% 0; } }

/* ==================== State icon + copy ==================== */
.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.state-icon svg { width: 30px; height: 30px; }
.state-icon-neutral { background: #f1f5f9; color: #64748b; }
.state-icon-danger  { background: #fef2f2; color: #dc2626; }
.state-icon-warn    { background: #fef3c7; color: #b45309; }
.state-icon-navy    { background: #eef2f7; color: #1b2a47; }
.state-title {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  line-height: 1.2;
}
.state-sub {
  font-size: 13.5px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
  max-width: 340px;
}
.state-sub b { color: #1b2a47; }
.state-meta, .receipt-grid, .verify-panel, .bank-block {
  width: 100%;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12.5px;
}
.meta-row span { color: #8a8371; }
.meta-row strong { color: #1b2a47; font-weight: 700; text-align: right; word-break: break-all; }
.mono { font-family: "SFMono-Regular", Menlo, monospace; letter-spacing: 0.01em; }
.ok-chip { color: #047857; }

/* ==================== Approved ==================== */
.success-burst {
  position: relative;
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.burst-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 2px solid rgba(16, 185, 129, 0.35);
  animation: ringPop 1s ease-out forwards;
}
.burst-ring-2 { animation-delay: 0.18s; }
@keyframes ringPop {
  from { transform: scale(0.6); opacity: 1; }
  to   { transform: scale(1.5); opacity: 0; }
}
.success-check {
  width: 68px;
  height: 68px;
  border-radius: 999px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 14px 30px -12px rgba(5, 150, 105, 0.6);
  animation: checkIn 0.5s cubic-bezier(0.16, 1.4, 0.5, 1) forwards;
}
.success-check svg { width: 34px; height: 34px; }
.success-check-sm { width: 52px; height: 52px; }
.success-check-sm svg { width: 26px; height: 26px; }
@keyframes checkIn {
  from { transform: scale(0.4); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.receipt-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.receipt-amount {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  color: #1b2a47;
}
.receipt-verified {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  color: #047857;
}
.receipt-verified svg { width: 14px; height: 14px; }
.redirect-hint {
  font-size: 12px;
  color: #a8a08c;
  margin: 0;
}

/* ==================== Verifying ==================== */
.verify-pulse {
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pulse-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 2px solid rgba(27, 42, 71, 0.25);
  animation: pulse 2.4s ease-out infinite;
}
.pulse-ring-2 { animation-delay: 0.8s; }
.pulse-ring-3 { animation-delay: 1.6s; }
@keyframes pulse {
  from { transform: scale(0.65); opacity: 1; }
  to   { transform: scale(1.45); opacity: 0; }
}
.verify-core {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: linear-gradient(135deg, #1b2a47, #2c3e63);
  color: #ffd7a8;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 14px 30px -14px rgba(27, 42, 71, 0.55);
}
.verify-core svg { width: 30px; height: 30px; }
.verify-slow {
  font-size: 12.5px;
  line-height: 1.55;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 0;
}
.verify-live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  font-weight: 600;
  color: #8a8371;
}
.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #10b981;
  animation: blink 1.6s ease-in-out infinite;
}
@keyframes blink { 50% { opacity: 0.25; } }

/* ==================== Expired / proof-only ==================== */
.qr-switch-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #94a3b8;
}
.qr-switch-inline-label { font-weight: 500; }
.qr-mini-chip {
  padding: 3px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}
.qr-mini-chip:hover:not(:disabled) { border-color: #94a3b8; color: #334155; }
.qr-mini-chip:disabled { opacity: 0.5; cursor: default; }
.qr-mini-chip.is-unavailable { opacity: 0.45; text-decoration: line-through; }
.qr-mini-new { border-style: dashed; }
.qr-mini-wait { font-size: 11px; }
.qr-switch-inline-notice {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  margin: 6px 0 0;
}

.qr-switch-bar {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
}
.qr-switch-row { display: flex; flex-wrap: wrap; gap: 8px; }
.qr-slot {
  padding: 7px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.qr-slot:disabled { cursor: default; opacity: 0.9; }
.qr-slot.is-current { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
.qr-slot.is-unavailable { opacity: 0.45; text-decoration: line-through; cursor: not-allowed; }
.qr-slot-new { border-style: dashed; color: #475569; }
.qr-slot-tag {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
}
.qr-switch-note { margin: 8px 0 0; font-size: 12px; color: #64748b; }
.qr-switch-notice {
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #b45309;
}

.expired-note {
  font-size: 11.5px;
  color: #a8a08c;
  text-align: center;
  margin: 0;
}

/* ==================== Payable ==================== */
.pay-hero {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}
.hero-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
}
.hero-amount {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.hero-countdown {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #eef2f7;
  color: #1b2a47;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.hero-countdown svg { width: 14px; height: 14px; }
.hero-countdown.is-urgent {
  background: #fef2f2;
  color: #dc2626;
  animation: blink 1.2s ease-in-out infinite;
}

/* Steps */
.steps {
  list-style: none;
  display: flex;
  padding: 0;
  margin: 0;
  gap: 4px;
}
.step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  position: relative;
  padding-top: 2px;
}
.step:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 13px;
  left: calc(50% + 16px);
  right: calc(-50% + 16px);
  height: 2px;
  background: #ede9df;
}
.step-dot {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #f4f0e5;
  color: #8a8371;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.step.is-current .step-dot {
  background: linear-gradient(135deg, #1b2a47, #2c3e63);
  color: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.12);
}
.step.is-done .step-dot { background: #d1fae5; color: #047857; }
.step-label {
  font-size: 10px;
  font-weight: 600;
  color: #8a8371;
  text-align: center;
  line-height: 1.25;
}
.step.is-current .step-label { color: #1b2a47; }

/* Payment section */
.pay-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.qr-frame {
  padding: 12px;
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 16px;
  box-shadow: 0 10px 24px -18px rgba(27, 42, 71, 0.4);
}
.qr-img { width: 180px; height: auto; display: block; }
.qr-caption {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
.qr-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #1b2a47;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: inherit;
  text-align: center;
}
.qr-download svg { width: 13px; height: 13px; flex-shrink: 0; }

.upi-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  padding: 10px 12px;
}
.upi-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.upi-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a08c;
}
.upi-value {
  font-size: 13px;
  font-weight: 700;
  color: #1b2a47;
  word-break: break-all;
}
.upi-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 9px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1b2a47;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  flex-shrink: 0;
  transition: all 0.15s;
}
.upi-copy:hover { background: #eef2f7; }
.upi-copy svg { width: 13px; height: 13px; }

.apps-block { display: flex; flex-direction: column; gap: 8px; }
.apps-title {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}
.apps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.app-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 4px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ede9df;
  color: #1b2a47;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s;
}
.app-btn:active { transform: scale(0.97); background: #f4f0e5; }
.apps-fallback {
  font-size: 11.5px;
  line-height: 1.5;
  color: #8a8371;
  margin: 0;
  text-align: center;
}

.bank-title {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

/* Proof panel */
.proof-wrap {
  border: 1px solid #ede9df;
  border-radius: 16px;
  padding: 14px;
  background: #fbfaf7;
  position: relative;
  transition: border-color 0.3s, box-shadow 0.3s, background 0.3s;
}
.proof-wrap.is-emphasized {
  border-color: #1b2a47;
  background: #fff;
  box-shadow: 0 14px 32px -22px rgba(27, 42, 71, 0.5);
}
.proof-wrap-open { background: #fff; }
.proof-head { margin-bottom: 12px; position: relative; }
.proof-flash {
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #c73443;
  animation: blink 1.2s ease-in-out infinite;
}
.proof-title {
  font-size: 14px;
  font-weight: 800;
  color: #1b2a47;
  line-height: 1.3;
}
.proof-sub {
  font-size: 12px;
  line-height: 1.55;
  color: #64748b;
  margin-top: 4px;
}

/* Proof form internals (rendered by the inline component) */
:deep(.pf) { display: flex; flex-direction: column; gap: 12px; }
:deep(.pf-utr) { display: flex; flex-direction: column; gap: 8px; }
:deep(.pf-label) {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8371;
}
:deep(.pf-input) {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  text-align: center;
  background: #fff;
  color: #0f172a;
  outline: none;
  /* Mono directly here — the parent's scoped `.mono` rule can't reach a
     child component's DOM (no data-v attribute), so the UTR digits get
     their monospace face from this :deep rule instead. */
  font-family: "SFMono-Regular", Menlo, monospace;
  letter-spacing: 0.04em;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}
:deep(.pf-input:focus) {
  border-color: #1b2a47;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
:deep(.pf-or) {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #a8a08c;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
:deep(.pf-or::before), :deep(.pf-or::after) {
  content: "";
  flex: 1;
  height: 1px;
  background: #ede9df;
}
:deep(.pf-hidden) { display: none; }
:deep(.pf-drop) {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 14px;
  border: 1.5px dashed #d9d3c1;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}
:deep(.pf-drop:hover) { border-color: #1b2a47; background: #fbfaf7; }
:deep(.pf-drop-icon) { font-size: 20px; }
:deep(.pf-drop-title) { font-size: 13px; font-weight: 700; color: #1b2a47; }
:deep(.pf-drop-sub) { font-size: 11px; color: #a8a08c; }
:deep(.pf-preview) { display: flex; flex-direction: column; gap: 10px; }
:deep(.pf-preview-img) {
  max-height: 160px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid #ede9df;
  cursor: pointer;
}
:deep(.pf-preview-actions) { display: flex; gap: 8px; justify-content: center; }
:deep(.pf-error) {
  font-size: 12px;
  color: #dc2626;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

/* Buttons */
:deep(.btn-primary), .btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #1b2a47 0%, #2c3e63 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  box-shadow: 0 10px 22px -10px rgba(27, 42, 71, 0.5);
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
}
:deep(.btn-primary:disabled) { opacity: 0.65; cursor: not-allowed; }
:deep(.btn-primary:not(:disabled):active) { transform: translateY(1px); }
:deep(.btn-block), .btn-block { width: 100%; }
:deep(.btn-ghost) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

/* Secure footer */
.secure-foot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 600;
  color: #a8a08c;
}
.secure-foot svg { width: 13px; height: 13px; }

/* ==================== Banner + submit burst ==================== */
.pp-banner {
  position: fixed;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  z-index: 60;
  max-width: 92vw;
  box-shadow: 0 16px 34px -16px rgba(15, 23, 42, 0.4);
}
.pp-banner.is-error { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
.pp-banner.is-info { background: #eef2f7; color: #1b2a47; border: 1px solid #cbd5e1; }
.banner-close {
  background: none;
  border: none;
  color: currentColor;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.7;
  font-family: inherit;
}
.submit-burst {
  position: fixed;
  inset: 0;
  background: rgba(251, 250, 247, 0.92);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
}
.burst-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.burst-title { font-size: 17px; font-weight: 800; color: #1b2a47; }
.burst-sub { font-size: 13px; color: #64748b; }

/* ==================== Transitions ==================== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ==================== Wider screens ==================== */
@media (min-width: 640px) {
  .pp-main { max-width: 480px; padding-top: 40px; }
  .pp-card { padding: 28px 26px; }
}
</style>
